import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { collection, query, where, getDocs, getDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import HeaderActionsPortal from './HeaderActionsPortal';
import {
  ClipboardList, Briefcase, Heart, RefreshCw, AlertTriangle, CalendarDays, Repeat, ChevronDown,
} from 'lucide-react';
import { resolveChecklist } from '../senInvigilation';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

// The assignee's side of the four duty modules, in one place.
//
// Teaching Duty, Admin Duty, Invigilation, Patrol and Substitution are assignment tools: their tabs
// are Allocation, Schedule, Statistics, Settings, and every one of them shows the whole school's
// grid. None of them has a "mine" view. That left a school choosing between leaving them switched
// off -- in which case a teacher cannot see their own duties at all, or even record a patrol
// preference -- and switching them on for everyone, which hands every teacher the machinery for
// reassigning their colleagues.
//
// So this is a separate module rather than a tab inside each of them, on the same reasoning the
// platform already settled for the family screens: one audience per module means one query shape
// per collection, and a rule then has exactly one thing to prove.
//
// Read access to all of these is staff-wide by design -- a duty roster is published, not secret --
// so the filtering here is for the person's benefit, not a security boundary. The boundary is on
// write, and it lives in firestore.rules.

const TODAY = () => new Date().toLocaleDateString('en-CA');

const card = (isMobile) => ({
  backgroundColor: 'var(--color-bg-card)',
  borderRadius: isMobile ? 0 : 'var(--radius-lg)',
  boxShadow: isMobile ? 'none' : 'var(--shadow-sm)',
  overflow: 'hidden',
});

// Gold marks the selection, the same as everywhere else in the portal -- not a status colour,
// because which half of your history you are looking at is not a success or a warning.
const segStyle = (on) => ({
  padding: '0.28rem 0.75rem',
  borderRadius: 'var(--radius-md)',
  fontSize: '0.82rem',
  fontWeight: on ? '700' : '500',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  flexShrink: 0,
  backgroundColor: on ? 'var(--color-nav-active)' : 'transparent',
  border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-border)'}`,
  color: on ? 'var(--color-text-main)' : 'var(--color-text-subtle)',
});

const KIND = {
  invigilation: { label: 'Invigilation', colour: 'var(--color-info)' },
  sen_invigilation: { label: 'SEN Invigilation', colour: 'var(--color-primary)' },
  patrol: { label: 'Patrol', colour: 'var(--color-accent)' },
  covering: { label: 'Covering', colour: 'var(--color-warning-text)' },
  covered: { label: 'Away — covered', colour: 'var(--color-text-muted)' },
};

const MyDuties = () => {
  const { activeSchoolId, isMobile, user } = useOutletContext();
  const { t } = useT();
  const ownEmail = (user?.email || '').toLowerCase();

  const [activeTab, setActiveTab] = useState('upcoming');
  const [ledgerView, setLedgerView] = useState('all');
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [dateView, setDateView] = useState('upcoming');
  // The two filters are independent on purpose: "what am I doing next" and "what kind of work" are
  // different questions, and a teacher usually wants to hold one steady while changing the other.
  const [kindView, setKindView] = useState('all');

  const [ownInitial, setOwnInitial] = useState('');
  const [roles, setRoles] = useState([]);
  const [dated, setDated] = useState([]);
  // Which duty is open. One at a time: this is a list you scan, not a dashboard.
  const [openUid, setOpenUid] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [preference, setPreference] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [recordedBalance, setRecordedBalance] = useState(null);
  const [savingPref, setSavingPref] = useState(false);
  // Set when a record was found only by initial. That means migrateDutyIdentity has not run here,
  // and the honest thing is to say so: everything below still works, but the rules protecting these
  // records cannot, because an initial is not an identity they can verify.
  const [matchedByInitialOnly, setMatchedByInitialOnly] = useState(0);

  // Two queries per collection, merged. Duty records carry an address only after the migration, and
  // querying the new field alone would return nothing at all beforehand -- a silent zero, which is
  // the failure this codebase has been bitten by often enough to have a name for. Matching on both
  // means the screen is correct before and after, and the count above says which happened.
  const pair = useCallback(async (col, emailField, initialField, arrayMatch) => {
    const ref = collection(db, 'schools', activeSchoolId, col);
    const op = arrayMatch ? 'array-contains' : '==';
    const found = new Map();
    let byInitial = 0;
    const [a, b] = await Promise.all([
      ownEmail ? getDocs(query(ref, where(emailField, op, ownEmail))) : Promise.resolve({ forEach: () => {} }),
      ownInitial ? getDocs(query(ref, where(initialField, op, ownInitial))) : Promise.resolve({ forEach: () => {} }),
    ]);
    a.forEach(d => found.set(d.id, { uid: d.id, ...d.data() }));
    b.forEach(d => { if (!found.has(d.id)) { found.set(d.id, { uid: d.id, ...d.data() }); byInitial += 1; } });
    return { rows: [...found.values()], byInitial };
  }, [activeSchoolId, ownEmail, ownInitial]);

  // Own initial, for the fallback half of every query above.
  useEffect(() => {
    if (!activeSchoolId || !ownEmail) return;
    getDoc(doc(db, 'schools', activeSchoolId, 'staff_profiles', ownEmail))
      .then(snap => setOwnInitial(snap.exists() ? String(snap.data().initial || '') : ''))
      .catch(() => setOwnInitial(''));
  }, [activeSchoolId, ownEmail]);

  // Fetched rather than subscribed. Six collections would be ten listeners for data that changes
  // weekly, and the one case that moves during the day -- a cover lesson arranged at 8am -- is
  // served by the Refresh button, which also tells you it happened.
  useEffect(() => {
    if (!activeSchoolId || !ownEmail) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const [teach, adminD, invig, senInvig, patrol, covering, covered, absences,
          prefSnap, patrolCfg, subCfg, balCfg] = await Promise.all([
          pair('teach_duty', 'staffEmail', 'initial', false),
          pair('admin_duty', 'staffEmail', 'initial', false),
          pair('invigilation_session', 'invigilatorEmails', 'invigilators', true),
          pair('sen_invigilation', 'invigilatorEmails', 'invigilators', true),
          pair('patrol_assignment', 'staffEmails', 'staff', true),
          pair('lesson_substitutions', 'substituteEmail', 'substituteStaffId', false),
          pair('lesson_substitutions', 'absentEmail', 'absentStaffId', false),
          pair('staff_absences', 'staffEmail', 'staffId', false),
          getDoc(doc(db, 'schools', activeSchoolId, 'patrol_preference', ownEmail)),
          getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'patrol_settings')),
          getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'substitution_settings')),
          getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'substitution_balances')),
        ]);
        if (cancelled) return;

        setMatchedByInitialOnly(
          teach.byInitial + adminD.byInitial + invig.byInitial + senInvig.byInitial
          + patrol.byInitial + covering.byInitial + covered.byInitial + absences.byInitial,
        );

        // The two things that move a substitution balance, in one list. Credits come from cover
        // lessons taken, debits from absences logged -- and both are stored as their own records, so
        // this is the ledger the accumulator in metadata/substitution_balances was built from.
        const reasons = (subCfg.exists() ? subCfg.data().absenceReasons : []) || [];
        const reasonLabel = (id) => (reasons.find(r => r.id === id)?.reason) || id || t('myDuties.absence');
        setLedger([
          ...covering.rows.map(r => ({
            uid: `c-${r.uid}`, kind: 'credit', date: r.date || '',
            amount: Number(r.reward || 0),
            title: [r.class, r.subj].filter(Boolean).join(' ') || t('myDuties.coverDuty'),
            detail: [r.periodCode, r.room, r.absentStaffId ? `for ${r.absentStaffId}` : '', r.remarks]
              .filter(Boolean).join(' · '),
          })),
          ...absences.rows.map(r => ({
            uid: `d-${r.uid}`, kind: 'debit', date: r.date || '',
            amount: -Number(r.deduction || 0),
            title: reasonLabel(r.reasonId),
            detail: r.remarks || '',
          })),
        ].sort((a, b) => String(b.date).localeCompare(String(a.date))));

        // Read by initial: the balances document is one map keyed that way, and unlike the duty
        // records it is not something this migration re-keys.
        setRecordedBalance(
          balCfg.exists() && ownInitial ? Number(balCfg.data()[ownInitial] || 0) : null,
        );

        setRoles([
          ...teach.rows.map(r => ({ ...r, kindLabel: t('myDuties.teaching') })),
          ...adminD.rows.map(r => ({ ...r, kindLabel: t('myDuties.administrative') })),
        ].sort((a, b) => String(b.sch_yr || '').localeCompare(String(a.sch_yr || ''))
          || String(a.department || '').localeCompare(String(b.department || ''))));

        const sess = (patrolCfg.exists() ? patrolCfg.data().sessions : []) || [];
        setSessions(sess);
        const sessionLabel = (id) => (sess.find(x => x.id === id)?.label) || id || 'Patrol';

        setDated([
          ...invig.rows.map(r => ({
            uid: `inv-${r.uid}`, kind: 'invigilation', date: r.date || '',
            title: r.subject || t('myDuties.examination'),
            detail: [r.venue, [r.startTime, r.endTime].filter(Boolean).join('–')].filter(Boolean).join(' · '),
          })),
          // A special room is the one duty here that comes with instructions, so it is the one that
          // opens. Saying "3-step running order" and stopping there would tell the invigilator that
          // something exists which they cannot read -- worse than saying nothing.
          ...senInvig.rows.map(r => ({
            uid: `sen-${r.uid}`, kind: 'sen_invigilation', date: r.date || '',
            title: [r.class, r.subject, r.paper].filter(Boolean).join(' ') || t('myDuties.specialRoom'),
            detail: [
              r.venue,
              [r.startTime, r.endTime].filter(Boolean).join('-'),
              r.extraMinutes ? `+${r.extraMinutes} min` : '',
              `${(r.students || []).length} student${(r.students || []).length === 1 ? '' : 's'}`,
            ].filter(Boolean).join(' · '),
            // Everything below is already on the room document -- staff-readable precisely so the
            // person standing in the room can read it. The register behind it stays shut.
            expand: {
              students: r.students || [],
              checklist: resolveChecklist(r.checklist || [], { startTime: r.startTime, endTime: r.endTime }),
              notes: r.notes || '',
            },
          })),
          ...patrol.rows.map(r => ({
            uid: `pat-${r.uid}`, kind: 'patrol', date: r.date || '',
            title: sessionLabel(r.sessionId), detail: '',
          })),
          ...covering.rows.map(r => ({
            uid: `cov-${r.uid}`, kind: 'covering', date: r.date || '',
            title: [r.class, r.subj].filter(Boolean).join(' ') || t('myDuties.coverDuty'),
            detail: [r.periodCode, r.room, [r.start_time, r.end_time].filter(Boolean).join('–'),
              r.absentStaffId ? `for ${r.absentStaffId}` : ''].filter(Boolean).join(' · '),
          })),
          ...covered.rows.map(r => ({
            uid: `abs-${r.uid}`, kind: 'covered', date: r.date || '',
            title: [r.class, r.subj].filter(Boolean).join(' ') || t('myDuties.yourLesson'),
            detail: [r.periodCode, r.substituteStaffId ? `covered by ${r.substituteStaffId}` : 'not yet covered']
              .filter(Boolean).join(' · '),
          })),
        ].sort((a, b) => String(a.date).localeCompare(String(b.date))));

        setPreference(prefSnap.exists() ? (prefSnap.data().sessionIds || []) : []);
      } catch (e) {
        console.error('Error loading my duties', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [activeSchoolId, ownEmail, ownInitial, reloadKey, pair]);

  const today = TODAY();
  // Undated records sit with the upcoming ones: a duty with no date has not happened yet as far as
  // anyone can tell, and burying it in a history nobody opens is how it gets missed.
  const upcoming = useMemo(
    () => dated.filter(d => !d.date || d.date >= today),
    [dated, today],
  );
  // Reversed. History is read newest-first -- "what did I do last week" is a question about the
  // recent end, and the alternative is scrolling through September to reach it.
  const past = useMemo(
    () => dated.filter(d => d.date && d.date < today).reverse(),
    [dated, today],
  );
  const byDate = dateView === 'past' ? past : upcoming;
  const shown = useMemo(
    () => (kindView === 'all' ? byDate : byDate.filter(d => d.kind === kindView)),
    [byDate, kindView],
  );

  // A chip for every kind this person has EVER been given, not just the ones in the half currently
  // shown. Chips that appear and vanish as you flip between upcoming and past make the row jump
  // under the pointer, and a "Patrol 0" is a real answer to "am I on patrol duty this term".
  const kindChips = useMemo(() => {
    const order = Object.keys(KIND);
    const present = new Set(dated.map(d => d.kind));
    return order
      .filter(k => present.has(k))
      .map(k => ({ id: k, label: kindLabel(k), colour: KIND[k].colour, n: byDate.filter(d => d.kind === k).length }));
  }, [dated, byDate]);

  const ledgerShown = useMemo(
    () => (ledgerView === 'all' ? ledger : ledger.filter(l => l.kind === ledgerView)),
    [ledger, ledgerView],
  );
  const credits = useMemo(() => ledger.filter(l => l.kind === 'credit'), [ledger]);
  const debits = useMemo(() => ledger.filter(l => l.kind === 'debit'), [ledger]);
  const ledgerTotal = useMemo(() => ledger.reduce((sum, l) => sum + l.amount, 0), [ledger]);

  const togglePreference = async (sessionId) => {
    const next = preference.includes(sessionId)
      ? preference.filter(s => s !== sessionId)
      : [...preference, sessionId];
    setSavingPref(true);
    try {
      // Written to a document keyed by the caller's own address, which is what the rule checks. No
      // other document on this screen is writable from here.
      await setDoc(
        doc(db, 'schools', activeSchoolId, 'patrol_preference', ownEmail),
        { sessionIds: next, initial: ownInitial, staffEmail: ownEmail },
        { merge: true },
      );
      setPreference(next);
    } catch (e) {
      console.error('Error saving patrol preference', e);
      alert(t('myDuties.patrolSaveFailed', { message: e.message }));
    } finally {
      setSavingPref(false);
    }
  };

  // KIND holds the English label; this is where it becomes the reader's language.
  const kindLabel = (k) => t(`myDuties.kinds.${k}`, {}, KIND[k]?.label || k);

  const tabs = [
    { id: 'upcoming', label: t('myDuties.tabs.upcoming'), icon: CalendarDays },
    { id: 'substitution', label: t('myDuties.tabs.substitution'), icon: Repeat },
    { id: 'roles', label: t('myDuties.tabs.roles'), icon: Briefcase },
    { id: 'preference', label: t('myDuties.tabs.patrolPreference'), icon: Heart },
  ];

  const empty = (text) => (
    <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.88rem' }}>
      {text}
    </div>
  );

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="theme-tabs-bar" style={{ minHeight: '66px' }}>
        <div className="theme-tabs-group">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`theme-tab ${activeTab === tab.id ? 'active' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                <div className="theme-tab-icon"><Icon size={18} /></div>
                {tab.label}
              </button>
            );
          })}
        </div>

        <HeaderActionsPortal>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button className="btn-outline" onClick={() => setReloadKey(k => k + 1)} disabled={loading}
                    title={t('myDuties.arrangedDuringDay')}
                    style={{ width: 'auto', padding: '0.4rem 0.9rem', whiteSpace: 'nowrap' }}>
              <RefreshCw size={15} /> {loading ? t('common.loading') : t('myDuties.refresh')}
            </button>
          </div>
        </HeaderActionsPortal>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? '0.75rem' : '0 1.5rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Not a warning about this screen -- everything below is correct. It is the only place a
            school will ever be told the duty records still identify people by initial, which is why
            it names the fix instead of just flagging a state. */}
        {matchedByInitialOnly > 0 && (
          <div style={{ ...card(isMobile), padding: '0.75rem 1rem', display: 'flex', gap: '0.6rem', alignItems: 'flex-start', backgroundColor: 'var(--color-warning-bg)' }}>
            <AlertTriangle size={17} style={{ flexShrink: 0, color: 'var(--color-warning-text)', marginTop: '0.1rem' }} />
            <div style={{ fontSize: '0.82rem', color: 'var(--color-warning-text)', lineHeight: 1.6 }}>
              {t('myDuties.matchedByInitial', {
                count: matchedByInitialOnly,
                task: t('myDuties.stampTask'),
              })}
            </div>
          </div>
        )}

        {activeTab === 'upcoming' && (
          <div style={card(isMobile)}>
            {/* Two lists, not one list with the past faded into it. Merged and sorted by date, a
                year of history sits on top of the thing you opened this to find. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: isMobile ? '0.7rem 0.85rem' : '0.85rem 1.25rem', borderBottom: '1px solid var(--color-border)' }}>
              <div style={{ display: 'flex', gap: '0.35rem' }}>
                {[['upcoming', 'Upcoming', upcoming.length], ['past', 'Past', past.length]].map(([id, label, n]) => (
                  <button key={id} type="button" onClick={() => setDateView(id)} style={segStyle(dateView === id)}>
                    {label}
                    <span style={{ marginLeft: '0.35rem', opacity: 0.75, fontWeight: '500' }}>{n}</span>
                  </button>
                ))}
              </div>

              {/* Only worth showing once there is more than one kind to tell apart. */}
              {kindChips.length > 1 && (
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => setKindView('all')} style={segStyle(kindView === 'all')}>
                    All
                    <span style={{ marginLeft: '0.35rem', opacity: 0.75, fontWeight: '500' }}>{byDate.length}</span>
                  </button>
                  {kindChips.map(k => (
                    <button
                      key={k.id} type="button" onClick={() => setKindView(k.id)}
                      style={{
                        ...segStyle(kindView === k.id),
                        // The same colour that marks the kind down the left of each row, so the chip
                        // and the rows it filters to are recognisably the same thing.
                        borderLeft: `3px solid ${k.colour}`,
                        // A kind with nothing in this half is still worth showing (see kindChips),
                        // but it should not look like a live choice.
                        opacity: k.n === 0 && kindView !== k.id ? 0.55 : 1,
                      }}
                    >
                      {k.label}
                      <span style={{ marginLeft: '0.35rem', opacity: 0.75, fontWeight: '500' }}>{k.n}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {loading ? empty(t('common.loading'))
              : shown.length === 0 ? empty(
                // An empty list has to say which filter emptied it, or it reads as "you have no
                // duties" when really it is "you have none of THIS kind, in THIS half".
                dated.length === 0
                  ? t('myDuties.noneAssigned')
                  : kindView !== 'all'
                    ? t(dateView === 'past' ? 'myDuties.noKindPast' : 'myDuties.noKindUpcoming', { kind: kindLabel(kindView) })
                    : dateView === 'past'
                      ? t('myDuties.nonePast')
                      : t('myDuties.noneUpcoming'))
              : shown.map(d => {
                const meta = KIND[d.kind];
                const canOpen = !!d.expand;
                const isOpen = canOpen && openUid === d.uid;
                return (
                  <div key={d.uid} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <div
                      onClick={canOpen ? () => setOpenUid(isOpen ? null : d.uid) : undefined}
                      style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', padding: isMobile ? '0.7rem 0.85rem' : '0.75rem 1.25rem', cursor: canOpen ? 'pointer' : 'default' }}
                    >
                      <div style={{ width: '4px', alignSelf: 'stretch', borderRadius: '2px', backgroundColor: meta.colour, flexShrink: 0 }} />
                      <div style={{ minWidth: '5.5rem', flexShrink: 0 }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: '700' }}>{d.date || '—'}</div>
                        <div style={{ fontSize: '0.72rem', fontWeight: '700', color: meta.colour }}>{kindLabel(d.kind)}</div>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>{d.title}</div>
                        {d.detail && <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>{d.detail}</div>}
                      </div>
                      {canOpen && (
                        <ChevronDown
                          size={16}
                          style={{ flexShrink: 0, color: 'var(--color-text-muted)', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}
                        />
                      )}
                    </div>

                    {isOpen && (
                      <div style={{ padding: isMobile ? '0 0.85rem 0.85rem 1.6rem' : '0 1.25rem 1rem 2rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
                        {d.expand.checklist.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                            <span style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
                              {t('myDuties.runningOrder')}
                            </span>
                            {d.expand.checklist.map(it => (
                              <div key={it.id} style={{ display: 'flex', gap: '0.55rem', alignItems: 'baseline', fontSize: '0.82rem' }}>
                                <span style={{ fontWeight: '700', color: 'var(--color-primary)', fontVariantNumeric: 'tabular-nums', minWidth: '3rem' }}>
                                  {it.time || '--:--'}
                                </span>
                                <span>{it.text}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {d.expand.students.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                            <span style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
                              {t('myDuties.inThisRoom')}
                            </span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                              {d.expand.students.map(st => (
                                <span key={st.email} style={{ display: 'inline-flex', gap: '0.3rem', padding: '0.1rem 0.5rem', borderRadius: '999px', fontSize: '0.76rem', backgroundColor: 'var(--color-bg-subtle)', color: 'var(--color-text-subtle)' }}>
                                  <b>{[st.currentClass, st.classNumber].filter(Boolean).join('-')}</b>
                                  {st.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {d.expand.notes && (
                          <span style={{ fontSize: '0.8rem', color: 'var(--color-text-subtle)' }}>{d.expand.notes}</span>
                        )}

                        {d.expand.checklist.length === 0 && d.expand.students.length === 0 && !d.expand.notes && (
                          <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                            {t('myDuties.roomNotSet')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}

        {activeTab === 'substitution' && (
          <>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {[
                [t('myDuties.balance'), recordedBalance === null ? '—' : recordedBalance, t('myDuties.asRecorded')],
                [t('myDuties.lessonsCovered'), credits.length, t('myDuties.earned', { n: credits.reduce((s, c) => s + c.amount, 0) })],
                [t('myDuties.absences'), debits.length, t('myDuties.deducted', { n: Math.abs(debits.reduce((s, d) => s + d.amount, 0)) })],
              ].map(([label, value, note]) => (
                <div key={label} style={{ ...card(isMobile), flex: '1 1 180px', padding: '0.9rem 1.1rem' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', fontWeight: '700' }}>{label}</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', lineHeight: 1.2 }}>{value}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{note}</div>
                </div>
              ))}
            </div>

            {/* Stated, not accused. The balance is an accumulator the coordinator's screens add to
                and subtract from; the list below is the records those movements came from. They can
                legitimately differ -- importing cover duties by CSV writes the records without
                touching the accumulator -- so this says what both numbers are and leaves the
                judgement to the office rather than declaring one of them wrong. */}
            {recordedBalance !== null && recordedBalance !== ledgerTotal && (
              <div style={{ ...card(isMobile), padding: '0.75rem 1rem', display: 'flex', gap: '0.6rem', alignItems: 'flex-start', backgroundColor: 'var(--color-warning-bg)' }}>
                <AlertTriangle size={17} style={{ flexShrink: 0, color: 'var(--color-warning-text)', marginTop: '0.1rem' }} />
                <div style={{ fontSize: '0.82rem', color: 'var(--color-warning-text)', lineHeight: 1.6 }}>
                  {t('myDuties.balanceDiffers', { ledger: ledgerTotal, recorded: recordedBalance })}
                </div>
              </div>
            )}

            <div style={card(isMobile)}>
              <div style={{ display: 'flex', gap: '0.35rem', padding: isMobile ? '0.7rem 0.85rem' : '0.85rem 1.25rem', borderBottom: '1px solid var(--color-border)' }}>
                {[['all', t('common.all'), ledger.length], ['credit', t('myDuties.covered'), credits.length], ['debit', t('myDuties.absent'), debits.length]].map(([id, label, n]) => (
                  <button key={id} type="button" onClick={() => setLedgerView(id)} style={segStyle(ledgerView === id)}>
                    {label}
                    <span style={{ marginLeft: '0.35rem', opacity: 0.75, fontWeight: '500' }}>{n}</span>
                  </button>
                ))}
              </div>
              {loading ? empty(t('common.loading'))
                : ledgerShown.length === 0 ? empty(t('myDuties.noRecords'))
                : ledgerShown.map(l => (
                  <div key={l.uid} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', padding: isMobile ? '0.7rem 0.85rem' : '0.75rem 1.25rem', borderBottom: '1px solid var(--color-border)' }}>
                    <div style={{ width: '4px', alignSelf: 'stretch', borderRadius: '2px', flexShrink: 0, backgroundColor: l.kind === 'credit' ? 'var(--color-success)' : 'var(--color-danger)' }} />
                    <div style={{ minWidth: '5.5rem', flexShrink: 0, fontSize: '0.85rem', fontWeight: '700' }}>{l.date || '—'}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>{l.title}</div>
                      {l.detail && <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>{l.detail}</div>}
                    </div>
                    {/* The sign is spelled out. A bare "6" against an absence reads as six of
                        something gained. */}
                    <div style={{ fontSize: '0.95rem', fontWeight: '700', flexShrink: 0, color: l.kind === 'credit' ? 'var(--color-success-text)' : 'var(--color-danger-text)' }}>
                      {l.amount > 0 ? `+${l.amount}` : l.amount}
                    </div>
                  </div>
                ))}
            </div>
          </>
        )}

        {activeTab === 'roles' && (
          <div style={card(isMobile)}>
            {loading ? empty(t('common.loading'))
              : roles.length === 0 ? empty(t('myDuties.noPosts'))
              : roles.map(r => (
                <div key={`${r.kindLabel}-${r.uid}`} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', padding: isMobile ? '0.7rem 0.85rem' : '0.75rem 1.25rem', borderBottom: '1px solid var(--color-border)' }}>
                  <ClipboardList size={17} style={{ flexShrink: 0, color: 'var(--color-text-muted)' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>{r.role ? t(`dutyRoles.${r.role.toLowerCase().replace(/[^a-z]/g, '')}`, {}, r.role) : '—'}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                      {[r.kindLabel, r.department, r.sch_yr].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}

        {activeTab === 'preference' && (
          <div style={{ ...card(isMobile), padding: isMobile ? '0.85rem' : '1.25rem' }}>
            <p style={{ margin: '0 0 0.9rem', fontSize: '0.83rem', color: 'var(--color-text-muted)', lineHeight: 1.6, maxWidth: '60ch' }}>
              {t('myDuties.patrolIntro')}
            </p>
            {sessions.length === 0 ? empty(t('myDuties.noPatrolSessions')) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                {sessions.map(sess => (
                  <label key={sess.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.45rem 0.6rem', borderRadius: 'var(--radius-md)', cursor: savingPref ? 'default' : 'pointer', border: '1px solid var(--color-border)', backgroundColor: preference.includes(sess.id) ? 'var(--color-nav-active)' : 'transparent' }}>
                    <input type="checkbox" checked={preference.includes(sess.id)} disabled={savingPref}
                           onChange={() => togglePreference(sess.id)}
                           style={{ width: '15px', height: '15px', accentColor: 'var(--color-primary)', cursor: 'pointer' }} />
                    <span style={{ fontSize: '0.88rem', fontWeight: '600' }}>{sess.label || sess.id}</span>
                    {(sess.startTime || sess.endTime) && (
                      <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                        {[sess.startTime, sess.endTime].filter(Boolean).join('–')}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyDuties;
